/**
 * Canvas-based image stitcher for bundle listings. Composes up to 24
 * card front photos into a single JPEG that's used as the gallery
 * image when a Go-Live listing has multiple cards. eBay caps the
 * picture set at 12, so the panel acts as the cover image while the
 * individual front photos fill the remaining slots.
 *
 * Images must be same-origin to avoid tainting the canvas; the
 * /api/images endpoint is same-host through the nginx proxy so no
 * crossOrigin attribute is needed.
 */

export interface StitchOptions {
  width?: number;
  height?: number;
  background?: string;
  gap?: number;
  padding?: number;
}

const DEFAULTS: Required<StitchOptions> = {
  width: 1600,
  height: 1200,
  background: "#ffffff",
  gap: 12,
  padding: 16,
};

const MAX_CARDS = 24;

function gridFor(n: number): { cols: number; rows: number } {
  if (n <= 2) return { cols: 2, rows: 1 };
  if (n <= 4) return { cols: 2, rows: 2 };
  if (n <= 6) return { cols: 3, rows: 2 };
  if (n <= 8) return { cols: 4, rows: 2 };
  if (n <= 9) return { cols: 3, rows: 3 };
  if (n <= 12) return { cols: 4, rows: 3 };
  if (n <= 16) return { cols: 4, rows: 4 };
  if (n <= 20) return { cols: 5, rows: 4 };
  return { cols: 6, rows: 4 };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

export async function stitchBundlePanel(
  imageUrls: string[],
  options: StitchOptions = {},
): Promise<Blob> {
  if (imageUrls.length === 0) throw new Error("No images to stitch");
  const opts = { ...DEFAULTS, ...options };
  const urls = imageUrls.slice(0, MAX_CARDS);
  const { cols, rows } = gridFor(urls.length);

  const canvas = document.createElement("canvas");
  canvas.width = opts.width;
  canvas.height = opts.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2D canvas context");

  ctx.fillStyle = opts.background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const cellW =
    (canvas.width - opts.padding * 2 - opts.gap * (cols - 1)) / cols;
  const cellH =
    (canvas.height - opts.padding * 2 - opts.gap * (rows - 1)) / rows;

  const images = await Promise.all(urls.map(loadImage));
  images.forEach((img, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = opts.padding + col * (cellW + opts.gap);
    const y = opts.padding + row * (cellH + opts.gap);
    const scale = Math.min(cellW / img.naturalWidth, cellH / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    const dx = x + (cellW - w) / 2;
    const dy = y + (cellH - h) / 2;
    ctx.drawImage(img, dx, dy, w, h);
  });

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error("Canvas toBlob returned null"));
        else resolve(blob);
      },
      "image/jpeg",
      0.85,
    );
  });
}

// ─── Overlay compositor ────────────────────────────────────────────────────
//
// Given an existing panel Blob (typically the output of stitchBundlePanel),
// optionally paint a watermark (centered, full-canvas, aspect-fit) and/or a
// logo (one corner, sized as a fraction of the shorter canvas dim) on top
// and return a new JPEG Blob.

export type LogoPosition = "br" | "bl" | "tr" | "tl";

export interface OverlayOptions {
  /** Same-origin URL of the watermark image; undef = skip. */
  watermarkUrl?: string;
  /** 0..1. Default 0.35 (matches the Settings hint). */
  watermarkOpacity?: number;
  /** Same-origin URL of the logo image; undef = skip. */
  logoUrl?: string;
  /** 0..1. Default 0.9 — logos are meant to be visible. */
  logoOpacity?: number;
  /** Corner placement. Default "br" (bottom-right). */
  logoPosition?: LogoPosition;
  /** Fraction of min(canvas.width, canvas.height). Default 1/6 (~200 px). */
  logoSizeRatio?: number;
  /** Margin from the picked corner in px. Default = padding (16). */
  logoMargin?: number;
}

function drawAspectFit(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  boxX: number,
  boxY: number,
  boxW: number,
  boxH: number,
): void {
  const scale = Math.min(boxW / img.naturalWidth, boxH / img.naturalHeight);
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  const dx = boxX + (boxW - w) / 2;
  const dy = boxY + (boxH - h) / 2;
  ctx.drawImage(img, dx, dy, w, h);
}

export async function compositeOverlays(
  basePanelBlob: Blob,
  opts: OverlayOptions,
): Promise<Blob> {
  const baseUrl = URL.createObjectURL(basePanelBlob);
  try {
    const baseImg = await loadImage(baseUrl);

    const canvas = document.createElement("canvas");
    canvas.width = baseImg.naturalWidth || DEFAULTS.width;
    canvas.height = baseImg.naturalHeight || DEFAULTS.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get 2D canvas context");

    ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);

    if (opts.watermarkUrl) {
      const wm = await loadImage(opts.watermarkUrl);
      const prev = ctx.globalAlpha;
      ctx.globalAlpha = opts.watermarkOpacity ?? 0.35;
      drawAspectFit(ctx, wm, 0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = prev;
    }

    if (opts.logoUrl) {
      const logo = await loadImage(opts.logoUrl);
      const minDim = Math.min(canvas.width, canvas.height);
      const size = minDim * (opts.logoSizeRatio ?? 1 / 6);
      const margin = opts.logoMargin ?? DEFAULTS.padding;
      const pos = opts.logoPosition ?? "br";
      const x =
        pos === "tr" || pos === "br" ? canvas.width - size - margin : margin;
      const y =
        pos === "bl" || pos === "br" ? canvas.height - size - margin : margin;
      const prev = ctx.globalAlpha;
      ctx.globalAlpha = opts.logoOpacity ?? 0.9;
      drawAspectFit(ctx, logo, x, y, size, size);
      ctx.globalAlpha = prev;
    }

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) reject(new Error("Canvas toBlob returned null"));
          else resolve(blob);
        },
        "image/jpeg",
        0.85,
      );
    });
  } finally {
    URL.revokeObjectURL(baseUrl);
  }
}
